import type { DepositLinkView } from "@car/api/deposit-links";
import type { GatewayKey } from "@car/api/payments/config";
import { Alert, AlertDescription, AlertTitle } from "@car/ui/components/alert";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@car/ui/components/card";
import { useAppForm } from "@car/ui/components/form";
import { Label } from "@car/ui/components/label";
import { RadioGroup, RadioGroupItem } from "@car/ui/components/radio-group";
import { Separator } from "@car/ui/components/separator";
import { Skeleton } from "@car/ui/components/skeleton";
import { Spinner } from "@car/ui/components/spinner";
import { toEnDigits } from "@car/ui/lib/form-rules";
import { SEP, cn } from "@car/ui/lib/utils";
import {
  Alert02Icon,
  BankIcon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  PrinterIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { PayShell } from "@/components/pay-shell";
import { faDateTimeShort, faPlainNumber, faToman, toFaDigits } from "@/lib/format";
import { gatewayPostReturn } from "@/lib/gateway-return";
import { orpc } from "@/utils/orpc";

// لینک پرداخت بیعانه: سرنخ پنل مشتریان ندارد و کارشناس فروش این نشانی را برایش پیامک می‌کند. ورود
// لازم نیست؛ نشانه داخل نشانی اجازه پرداخت است. همین صفحه مقصد بازگشت از درگاه هم هست:
// پارامترهای بازگشت همان‌طور که آمده‌اند برای تأیید سمت سرور می‌روند و نتیجه فقط از پاسخ سرور
// خوانده می‌شود، نه از خود نشانی.
export const Route = createFileRoute("/d/$token")({
  ssr: false,
  // پارامترهای درگاه متن‌اند؛ مقداری که روتر عدد خوانده (مثلاً کد تراکنش) دوباره متن می‌شود
  validateSearch: z.record(z.string(), z.coerce.string()),
  server: { handlers: { POST: gatewayPostReturn } },
  head: () => ({
    meta: [{ title: "پرداخت آنلاین | تابان خودرو" }, { name: "robots", content: "noindex" }],
  }),
  component: DepositLinkPage,
});

const REFUND_NOTE = "اگر مبلغی از حساب شما کسر شده باشد، بانک حداکثر تا ۷۲ ساعت آن را بازمی‌گرداند.";
const CALL_SALES = "برای لینک تازه با کارشناس فروش تماس بگیرید.";

const tones = {
  ok: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  wait: "bg-amber-500/15 text-amber-800 dark:text-amber-400",
  bad: "bg-destructive/10 text-destructive",
} as const;

/** سربرگ هر وضعیت: نشانه رنگی، عنوان و یکی دو خط توضیح */
function Notice({
  tone,
  icon,
  title,
  children,
}: {
  tone: keyof typeof tones;
  icon: IconSvgElement;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="grid justify-items-center gap-2 text-center">
      <span className={cn("grid size-14 place-items-center rounded-full", tones[tone])}>
        <HugeiconsIcon icon={icon} strokeWidth={1.8} className="size-8" />
      </span>
      <h1 className="text-lg font-black">{title}</h1>
      {children ? <div className="grid gap-1 text-sm text-muted-foreground">{children}</div> : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-end font-bold">{children}</dd>
    </div>
  );
}

/** بابت، پرداخت‌کننده و مبلغ؛ در همه وضعیت‌ها یکسان */
function LinkFacts({ link, children }: { link: DepositLinkView; children?: ReactNode }) {
  return (
    <dl className="grid gap-2 rounded-lg bg-muted/60 p-4 text-sm">
      <Fact label="بابت">{link.title}</Fact>
      <Fact label="پرداخت‌کننده">{link.payerName}</Fact>
      {children}
      <Separator />
      <div className="flex justify-between gap-3 text-base">
        <dt className="font-bold">مبلغ</dt>
        <dd className="font-black">{faToman(link.amount)}</dd>
      </div>
    </dl>
  );
}

function DepositLinkPage() {
  return (
    <PayShell>
      <LinkBody />
    </PayShell>
  );
}

function LinkBody() {
  const { token } = Route.useParams();
  // `bank=test` پنل درگاه آزمایشی همین صفحه است؛ هر پارامتر دیگر را درگاه در بازگشت گذاشته است
  const { bank, ...params } = Route.useSearch();
  const navigate = useNavigate();
  const returned = Object.keys(params).length > 0;

  const verify = useMutation(
    orpc.depositLink.verify.mutationOptions({
      onSuccess: (result) => {
        // نتیجه قطعی شد (پرداخت شد یا بانک رد کرد): پارامترهای درگاه از نشانی برداشته می‌شوند.
        // نتیجه نامعلوم (بانک در دسترس نبود) آن‌ها را نگه می‌دارد تا «بررسی دوباره» یا بارگذاری
        // دوباره صفحه، تأیید را دوباره امتحان کند.
        if (result.ok || !result.retry) {
          navigate({ to: "/d/$token", params: { token }, search: {}, replace: true });
        }
      },
    }),
  );
  const sent = useRef(false);
  useEffect(() => {
    if (!returned || sent.current) return;
    sent.current = true;
    verify.mutate({ token, params });
  }, [returned, token, params, verify]);

  const view = useQuery({
    ...orpc.depositLink.get.queryOptions({ input: { token } }),
    // در بازگشت از درگاه، اول تأیید و بعد وضعیت ذخیره‌شده
    enabled: !returned || verify.isSuccess || verify.isError,
  });

  const refusal = verify.data && !verify.data.ok ? verify.data : null;
  const link = (verify.data?.ok ? verify.data.link : null) ?? view.data ?? null;
  // سرور یا بانک پاسخ نداد: هنوز معلوم نیست پولی جابه‌جا شده یا نه
  const unknown = verify.isError || (refusal?.retry ?? false);
  const verifying = returned && !verify.isSuccess && !verify.isError;

  if (verifying || (!unknown && !link && view.isPending)) {
    return (
      <Card>
        <CardContent className="grid justify-items-center gap-3 py-10 text-center">
          <Spinner className="size-6" />
          <p className="font-bold">{returned ? "در حال بررسی پرداخت…" : "در حال بارگذاری…"}</p>
          <Skeleton className="h-4 w-48" />
        </CardContent>
      </Card>
    );
  }

  if (unknown) {
    return (
      <Card>
        <CardContent className="grid gap-5 py-2">
          <Notice tone="wait" icon={Clock01Icon} title="نتیجه پرداخت هنوز مشخص نیست">
            <p>{refusal?.reason ?? "بررسی پرداخت انجام نشد؛ اتصال اینترنت را بررسی کنید."}</p>
            <p>
              اگر در درگاه پرداخت کرده‌اید، دوباره پرداخت نکنید؛ پرداخت شما تا چند دقیقه دیگر خودکار
              هم بررسی می‌شود.
            </p>
          </Notice>
          <Button onClick={() => verify.mutate({ token, params })}>بررسی دوباره</Button>
        </CardContent>
      </Card>
    );
  }

  if (!link) {
    // خطای بارگذاری با «پیدا نشد» یکی نیست: لینک شاید سالم باشد
    return (
      <Card>
        <CardContent className="grid gap-5 py-6">
          {view.isError ? (
            <>
              <Notice tone="wait" icon={Clock01Icon} title="صفحه بارگذاری نشد">
                <p>اتصال اینترنت را بررسی و دوباره تلاش کنید.</p>
              </Notice>
              <Button onClick={() => view.refetch()}>تلاش دوباره</Button>
            </>
          ) : (
            <Notice tone="bad" icon={CancelCircleIcon} title="این لینک پرداخت پیدا نشد">
              <p>نشانی را از پیامک دوباره باز کنید یا با کارشناس فروش تماس بگیرید.</p>
            </Notice>
          )}
        </CardContent>
      </Card>
    );
  }

  if (link.status === "paid") return <Receipt link={link} />;

  if (link.status !== "open") {
    const expired = link.status === "expired";
    return (
      <Card>
        <CardContent className="grid gap-5 py-2">
          <Notice
            tone="bad"
            icon={expired ? Clock01Icon : CancelCircleIcon}
            title={expired ? "مهلت این لینک پرداخت تمام شده است" : "این لینک پرداخت لغو شده است"}
          >
            <p>
              {expired ? `مهلت پرداخت ${faDateTimeShort(link.expiresIso)} بود. ` : ""}
              {CALL_SALES}
            </p>
            {refusal ? <p>{REFUND_NOTE}</p> : null}
          </Notice>
          <LinkFacts link={link} />
        </CardContent>
      </Card>
    );
  }

  if (bank === "test" && !returned && !refusal) return <TestBank token={token} link={link} />;

  return <PayCard token={token} link={link} failure={refusal?.reason ?? ""} />;
}

/** لینک باز: چه چیزی، چقدر، از کدام درگاه؛ `failure` دلیل ناموفق بودن تلاش قبلی است */
function PayCard({
  token,
  link,
  failure,
}: {
  token: string;
  link: DepositLinkView;
  failure: string;
}) {
  const [chosen, setChosen] = useState<GatewayKey | null>(null);
  const gateway = link.gateways.find((option) => option.key === chosen) ?? link.gateways[0];
  const start = useMutation(
    orpc.depositLink.start.mutationOptions({
      onSuccess: (result) => {
        if (!result.ok) {
          toast.error(result.reason);
          return;
        }
        window.location.assign(result.redirectUrl);
      },
      onError: () => toast.error("اتصال برقرار نشد؛ دوباره تلاش کنید."),
    }),
  );
  // تا رسیدن به صفحه بانک دکمه بسته می‌ماند
  const leaving = start.isPending || start.data?.ok === true;
  // درگاهی که دارنده کارت را می‌سنجد (سداد با کلید احراز هویت) کد ملی پرداخت‌کننده را می‌خواهد
  // فقط ده رقم، بی رقم کنترل: تطبیق با کارت کار شاپرک است و کد ملی آزمایشی سداد رقم کنترل درست ندارد
  const needsId = gateway?.nationalId ?? false;
  const nationalId = z
    .string()
    .refine((value) => value.trim() !== "", "کد ملی را وارد کنید")
    .refine((value) => /^\d{10}$/.test(toEnDigits(value.trim())), "کد ملی ده رقم است");
  const form = useAppForm({
    defaultValues: { nationalId: "" },
    validators: { onBlur: z.object({ nationalId: needsId ? nationalId : z.string() }) },
    onSubmit: ({ value }) => {
      if (!gateway) return;
      start.mutate({
        token,
        gateway: gateway.key,
        nationalId: needsId ? toEnDigits(value.nationalId.trim()) : undefined,
      });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>پرداخت آنلاین</CardTitle>
        <CardDescription>
          تابان خودرو{SEP}مهلت پرداخت تا {faDateTimeShort(link.expiresIso)}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        {failure ? (
          <Alert variant="destructive">
            <HugeiconsIcon icon={Alert02Icon} strokeWidth={1.8} />
            <AlertTitle>پرداخت انجام نشد</AlertTitle>
            <AlertDescription>
              {failure} {REFUND_NOTE}
            </AlertDescription>
          </Alert>
        ) : null}

        <LinkFacts link={link} />

        {!gateway ? (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            پرداخت آنلاین فعلاً فعال نیست؛ با کارشناس فروش تماس بگیرید.
          </p>
        ) : (
          <>
            {link.gateways.length > 1 ? (
              <div className="grid gap-2">
                <p className="text-sm font-bold">درگاه پرداخت</p>
                {/* form-kit-exempt: انتخاب درگاه پیش از رفتن به بانک، نه فیلد فرم */}
                <RadioGroup
                  aria-label="درگاه پرداخت"
                  value={gateway.key}
                  onValueChange={(value) =>
                    setChosen(link.gateways.find((option) => option.key === value)?.key ?? null)
                  }
                  className="gap-2"
                >
                  {link.gateways.map((option) => (
                    <Label
                      key={option.key}
                      className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 font-normal has-data-checked:border-primary"
                    >
                      <RadioGroupItem value={option.key} />
                      {option.label}
                      {option.sandbox ? <Badge variant="outline">آزمایشی</Badge> : null}
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            ) : null}

            {gateway.sandbox ? (
              <Alert>
                <HugeiconsIcon icon={Alert02Icon} strokeWidth={1.8} />
                <AlertTitle>{gateway.label} در حالت آزمایشی است</AlertTitle>
                <AlertDescription>
                  هیچ مبلغی از حساب شما برداشت نمی‌شود؛ این پرداخت فقط برای آزمایش ثبت می‌شود.
                </AlertDescription>
              </Alert>
            ) : null}

            <form.AppForm>
              <form.Form className="grid gap-5">
                {needsId ? (
                  <form.AppField name="nationalId">
                    {(field) => (
                      <field.TextField
                        label="کد ملی صاحب کارت"
                        description="درگاه فقط کارتی را می‌پذیرد که به نام همین کد ملی باشد."
                        inputMode="numeric"
                        dir="ltr"
                        maxLength={10}
                        autoComplete="off"
                      />
                    )}
                  </form.AppField>
                ) : null}
                <form.SubmitButton size="lg" pending={leaving}>
                  {failure ? "تلاش دوباره" : "پرداخت آنلاین"}
                </form.SubmitButton>
              </form.Form>
            </form.AppForm>
            <p className="text-center text-xs text-muted-foreground">
              پرداخت امن با {gateway.label}؛ پس از پرداخت به همین صفحه برمی‌گردید و رسید می‌گیرید.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Receipt({ link }: { link: DepositLinkView }) {
  const receipt = link.receipt;
  return (
    <Card className="print:border-0 print:shadow-none">
      <CardContent className="grid gap-5 py-2">
        <Notice tone="ok" icon={CheckmarkCircle02Icon} title="پرداخت انجام شد">
          <p>رسید این پرداخت نزد تابان خودرو ثبت شد و در «تراکنش‌ها»ی پنل شما هم می‌ماند.</p>
        </Notice>
        <LinkFacts link={link}>
          {receipt ? (
            <>
              <Fact label="شماره رسید">{faPlainNumber(receipt.number)}</Fact>
              <Fact label="کد پیگیری">
                <span dir="ltr" className="font-mono">
                  {toFaDigits(receipt.reference)}
                </span>
              </Fact>
              <Fact label="تاریخ پرداخت">{faDateTimeShort(receipt.paidIso)}</Fact>
            </>
          ) : null}
        </LinkFacts>
        {receipt?.sandbox ? (
          <Alert>
            <HugeiconsIcon icon={Alert02Icon} strokeWidth={1.8} />
            <AlertTitle>پرداخت آزمایشی</AlertTitle>
            <AlertDescription>
              این پرداخت در حالت آزمایشی درگاه ثبت شده و مبلغی از حساب شما برداشت نشده است.
            </AlertDescription>
          </Alert>
        ) : null}
        <Button variant="outline" className="print:hidden" onClick={() => window.print()}>
          <HugeiconsIcon icon={PrinterIcon} strokeWidth={1.8} />
          چاپ رسید
        </Button>
        <Button
          variant="ghost"
          className="print:hidden"
          nativeButton={false}
          render={<Link to="/panel/transactions" />}
        >
          تراکنش‌های من در پنل
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * درگاه آزمایشی (فقط محیط توسعه): نقش بانک را بازی می‌کند. تأیید یا رد با امضای سرور برای همین
 * لینک به همین صفحه برمی‌گردد و تأیید نهایی سمت سرور انجام می‌شود؛ پولی جابه‌جا نمی‌شود.
 */
function TestBank({ token, link }: { token: string; link: DepositLinkView }) {
  const navigate = useNavigate();
  const decide = useMutation(
    orpc.depositLink.testGateway.mutationOptions({
      onSuccess: (result) => {
        if (!result.ok) toast.error(result.reason);
        navigate({
          to: "/d/$token",
          params: { token },
          search: result.ok ? result.params : {},
          replace: true,
        });
      },
    }),
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
            <HugeiconsIcon icon={BankIcon} strokeWidth={1.8} />
          </span>
          <div>
            <CardTitle>درگاه پرداخت آزمایشی</CardTitle>
            <CardDescription>تابان خودرو{SEP}شبیه‌ساز بانک</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-5">
        <Alert variant="destructive">
          <HugeiconsIcon icon={Alert02Icon} strokeWidth={1.8} />
          <AlertTitle>این یک درگاه آزمایشی است</AlertTitle>
          <AlertDescription>
            هیچ مبلغی از کارت شما برداشت نمی‌شود. با «تأیید پرداخت» پرداخت موفق و با «رد پرداخت»
            پرداخت ناموفق شبیه‌سازی می‌شود.
          </AlertDescription>
        </Alert>
        <LinkFacts link={link} />
        <div className="grid gap-2 sm:grid-cols-2">
          <Button
            size="lg"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ token, approve: true })}
          >
            {decide.isPending && decide.variables?.approve ? <Spinner className="size-4" /> : null}
            تأیید پرداخت
          </Button>
          <Button
            size="lg"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ token, approve: false })}
          >
            {decide.isPending && !decide.variables?.approve ? <Spinner className="size-4" /> : null}
            رد پرداخت
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
